/**
 * Medical Condition Tags Component
 *
 * Reusable component for displaying medical conditions as styled tags.
 * Supports different variants and themes.
 *
 * @module components/ui/MedicalConditionTags
 */

import React from 'react';
import { useTheme } from '@/hooks/useTheme';
import { getHealthConditionLabels } from '@/constants/healthConditions';

export interface MedicalConditionTagsProps {
  /** Array of medical conditions to display */
  conditions: string[];
  /** Visual variant of the tags */
  variant?: 'default' | 'compact';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Medical Condition Tags Component
 */
export const MedicalConditionTags: React.FC<MedicalConditionTagsProps> = ({
  conditions,
  variant = 'default',
  className = ''
}) => {
  const { isDark } = useTheme();

  if (!conditions || conditions.length === 0) {
    return null;
  }

  const getTagClasses = () => {
    const baseClasses = 'rounded-full text-xs';

    if (variant === 'compact') {
      return `${baseClasses} px-2 py-1 ${
        isDark ? 'bg-secondary-800 text-secondary-200' : 'bg-secondary-100 text-secondary-700'
      }`;
    }

    // default variant
    return `${baseClasses} inline-flex items-center px-2.5 py-0.5 font-medium ${
      isDark
        ? 'bg-secondary-700 text-secondary-200'
        : 'bg-secondary-100 text-secondary-800'
    }`;
  };

  // Get human-readable labels for the conditions
  const conditionLabels = getHealthConditionLabels(conditions);

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {conditionLabels.map((label, index) => (
        <span
          key={index}
          className={getTagClasses()}
        >
          {label}
        </span>
      ))}
    </div>
  );
};

export default MedicalConditionTags;